import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { TextareaAutosize } from '@mui/material';
import { useParams } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import APIURL, { IMAGE_BASE_URL } from '../api/apiClient';
import Loader from './Loader';

const ServiceScreen = () => {
    const [loading, setLoading] = useState(false);
    const { id } = useParams();

    const [formData, setFormData] = useState({
        user_name: '',
        status: "",
        tentative_date: "",
        accept_service: false,
        require_more_details: false,
        estimated_amount: '',
        estimated_date: '',
        remarks: '',
        description: '',
        category_name: "",
        brand: "",
        manual_product_name: "",
        repair_title: "",
        image1: null,  // For first image preview
        image2: null,  // For second image preview
        customer_response: '',
    });

    const [showDateField, setShowDateField] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [originalStatus, setOriginalStatus] = useState('');
    const [errorMessage, setErrorMessage] = useState({})

    // Define styles as functions where needed
    const getDisabledInputStyles = {
        '& .MuiInputBase-input.Mui-disabled': {
            WebkitTextFillColor: '#000000',
            fontWeight: 500,
            opacity: 1
        },
        '& .MuiInputLabel-root.Mui-disabled': {
            color: '#666666'
        }
    };

    const getCustomerResponseStyles = (value) => ({
        '& .MuiInputBase-input.Mui-disabled': {
            fontWeight: 600,
            WebkitTextFillColor:
                value === 'Pending' ? '#FFA500' :
                    value === 'Accepted' ? '#008000' :
                        value === 'Rejected' ? '#FF0000' :
                            '#000000'
        }
    });

    const fetchServiceData = () => {
        setLoading(true);
        axios
            .get(`${APIURL.baseURL}/fetchserviceid.php?id=${id}`)
            .then((response) => {
                if (response.data) {
                    const baseImageUrl = `${IMAGE_BASE_URL}`;
                    const serviceData = {
                        ...response.data,
                        image1: response.data.image1
                            ? `${baseImageUrl}${response.data.image1}`
                            : null,
                        image2: response.data.image2
                            ? `${baseImageUrl}${response.data.image2}`
                            : null,
                    };

                    setFormData((prev) => ({
                        ...prev,
                        ...serviceData,
                    }));

                    setOriginalStatus(serviceData.status);
                }
            })
            .catch((error) => {
                console.error("Error fetching service data:", error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchServiceData();
    }, [id]);



    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'status') {
            const shouldShowDate = ['In progress', 'Accepted'].includes(value);
            setShowDateField(shouldShowDate);

            if (!shouldShowDate) {
                setFormData(prev => ({
                    ...prev,
                    tentative_date: ''
                }));
            }
        }

        // Reset fields when switching between options
        if (name === 'accept_service' && value) {
            setFormData(prev => ({
                ...prev,
                require_more_details: false,
                remarks: ''
            }));
        }

        if (name === 'require_more_details' && value) {
            setFormData(prev => ({
                ...prev,
                accept_service: false,
                estimated_amount: '',
                estimated_date: ''
            }));
        }

    };

    const handleUpdateStatus = async (id, status, tentative_date) => {
        if (status === originalStatus) {
            toast.info(`Status is already '${status}'`);
            return;
        }

        try {
            // Use APIURL base client
            const response = await APIURL.post('/updateservice.php', { id, status, tentative_date });

            if (response.success) {
                toast.success('Status updated successfully!');
                setOriginalStatus(status);
                setFormData(prev => ({
                    ...prev,
                    status
                }));
            } else {
                toast.error('Failed to update status');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Error updating status');
        }
    };


    const handleSubmitAction = async (e) => {
        e.preventDefault();

        // Clear previous error message
        setErrorMessage('');

        // Validation
        if (formData.accept_service) {
            if (!formData.estimated_amount) {
                setErrorMessage('Please enter estimated amount.');
                toast.warning('Estimated amount is required.');
                return;
            }
            if (!formData.estimated_date) {
                setErrorMessage('Please enter estimated date.');
                toast.warning('Estimated date is required.');
                return;
            }
        } else if (formData.require_more_details) {
            if (!formData.remarks) {
                setErrorMessage('Please enter remarks.');
                toast.warning('Remarks are required.');
                return;
            }
        }

        try {
            const payload = formData.accept_service
                ? {
                    id: formData.id,
                    status: 'Accepted',
                    estimated_amount: formData.estimated_amount,
                    estimated_date: formData.estimated_date
                }
                : {
                    id: formData.id,
                    status: 'Reopen',
                    remarks: formData.remarks
                };

            // Use APIURL.post to automatically prepend base URL and headers
            const response = await APIURL.post('/updateservice.php', payload);

            if (response.success) {
                toast.success('Service updated successfully!');
                setIsSubmitted(true);
                fetchServiceData();

                // Reset form fields after submission
                setFormData(prev => ({
                    ...prev,
                    accept_service: false,
                    require_more_details: false,
                    estimated_amount: '',
                    estimated_date: '',
                    remarks: ''
                }));
            } else {
                toast.error('Failed to update service');
            }
        } catch (error) {
            console.error('Error updating service:', error);
            toast.error('Error updating service');
        }
    };


    // ✅ Full screen loader while fetching
    if (loading) {
        return <Loader type="full" loading={loading} />;
    }

    return (
        <>
            {/* <Loader type='full' loading={loading} /> */}

            <div className='service-screen-container'>
                <ToastContainer position='top-right' autoClose={3000} hideProgressBar />
                <div className='card'>
                    <div className='card-header text-white d-flex justify-content-between align-items-center'
                        style={{
                            background: 'linear-gradient(15deg, #abe9cd 0%, #3eadcf 100%)',
                            padding: '1rem 1.5rem' // Increased padding
                        }}>
                        <h2 className='mb-0' style={{
                            color: '#000000',
                            textShadow: '0 1px 2px rgba(0,0,0,0.2)' // Subtle text shadow for better readability
                        }}>
                            Service Screen
                        </h2>
                        <span className='badge' style={{
                            background: originalStatus === "Pending"
                                ? 'linear-gradient(135deg, #ffb347, #ffcc33)'
                                : originalStatus === "In Progress"
                                    ? 'linear-gradient(135deg, #4b6cb7, #182848)'
                                    : originalStatus === "Completed"
                                        ? 'linear-gradient(135deg, #56ab2f, #a8e063)'
                                        : 'linear-gradient(135deg, #9e9e9e, #616161)',
                            color: originalStatus === "In_progress" ? 'white' : '#333',
                            padding: '0.5rem 1rem',
                            borderRadius: '20px',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            textTransform: 'capitalize'
                        }}>
                            {originalStatus}
                        </span>

                    </div>

                    <div className='card-body'>
                        <form>

                            {/* Accept Service / Require Details Section */}

                            {(["Pending", "Reopen"].includes(formData.status) && !isSubmitted) && (
                                <div className='border rounded p-4 mb-4'>
                                    <h5 className='mb-4 border-bottom pb-2'>Service Action</h5>
                                    <div className='d-flex justify-content-center'>
                                        <div className='col-md-6 text-center'>
                                            <div className='mb-3'>
                                                <button
                                                    type='button'
                                                    name='accept_service'
                                                    className='btn btn-success me-3'
                                                    onClick={() => setFormData(prev => ({
                                                        ...prev,
                                                        accept_service: true,
                                                        require_more_details: false
                                                    }))}
                                                >
                                                    Accept Service
                                                </button>
                                                <button
                                                    type='button'
                                                    name='require_more_details'
                                                    className='btn btn-warning'
                                                    onClick={() => setFormData(prev => ({
                                                        ...prev,
                                                        require_more_details: true,
                                                        accept_service: false
                                                    }))}
                                                >
                                                    Require more details
                                                </button>
                                            </div>

                                            {formData.accept_service && (
                                                <div className='row mt-3 justify-content-center'>
                                                    <div className='col-md-10'>
                                                        <div className='row'>
                                                            <div className='col-md-6'>
                                                                <TextField
                                                                    type='number'
                                                                    label="Estimated amount"
                                                                    variant="outlined"
                                                                    name='estimated_amount'
                                                                    value={formData.estimated_amount}
                                                                    onChange={handleChange}
                                                                    error={!!errorMessage.estimated_amount}
                                                                    helperText={errorMessage.estimated_amount}
                                                                    fullWidth
                                                                    size='small'
                                                                    margin="normal"
                                                                    required
                                                                />
                                                            </div>
                                                            <div className='col-md-6'>
                                                                <TextField
                                                                    type='date'
                                                                    label="Estimated date"
                                                                    variant='outlined'
                                                                    name="estimated_date"
                                                                    value={formData.estimated_date}
                                                                    onChange={handleChange}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    error={!!errorMessage.estimated_date}
                                                                    helperText={errorMessage.estimated_date}
                                                                    fullWidth
                                                                    margin="normal"
                                                                    size='small'
                                                                    required
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {formData.require_more_details && (
                                                <div className='row mt-3 justify-content-center'>
                                                    <div className='col-md-10'>
                                                        <TextField
                                                            label="Additional Details"
                                                            variant="outlined"
                                                            name="remarks"
                                                            value={formData.remarks}
                                                            onChange={handleChange}
                                                            error={!!errorMessage.remarks}
                                                            helperText={errorMessage.remarks}
                                                            multiline
                                                            rows={4}
                                                            fullWidth
                                                            margin="normal"
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {(formData.accept_service || formData.require_more_details) && (
                                                <div className='mt-3'>
                                                    <button type='submit' className='btn btn-info'
                                                        onClick={handleSubmitAction}>
                                                        Submit
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className='border rounded p-4 mb-4 position-relative'>
                                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
                                    <h5 className="mb-0">Service Details</h5>
                                    <h5 className="mb-0 text-uppercase" style={{ marginRight: '50px' }}>Name: {formData.user_name}</h5>
                                </div>




                                <div className='row'>
                                    <div className='col-md-8'>
                                        <div className='row'>
                                            <div className='col-md-12 mb-3'>
                                                <div className='row align-items-end'>
                                                    {/* STATUS SELECT */}
                                                    <div className={showDateField ? 'col-md-6' : 'col-md-6 d-flex align-items-end justify-content-between'}>
                                                        <TextField
                                                            select
                                                            label="Service Status"
                                                            fullWidth
                                                            variant="outlined"
                                                            name='status'
                                                            value={formData.status}
                                                            onChange={handleChange}
                                                            size="small"
                                                            margin="normal"
                                                        >
                                                            <MenuItem value="Pending" disabled>Pending</MenuItem>
                                                            <MenuItem value="In Progress">In progress</MenuItem>
                                                            <MenuItem value="Accepted">Accepted</MenuItem>
                                                            <MenuItem value="Completed">Service Completed-Ready for Pickup</MenuItem>
                                                            <MenuItem value="Reopen">Reopen</MenuItem>
                                                            <MenuItem value="Rejected">Rejected</MenuItem>
                                                        </TextField>

                                                        {/* ✅ Show Check Button on the right when no tentative date */}
                                                        {!showDateField && (
                                                            <button
                                                                className='ms-2'
                                                                style={{
                                                                    borderRadius: "5px",
                                                                    padding: '5px 15px',
                                                                    fontSize: '18px',
                                                                    border: 'none',
                                                                    marginBottom: '10px',
                                                                    backgroundColor: "#007bff",
                                                                    color: "#fff",
                                                                    height: '40px'
                                                                }}
                                                                onClick={() => handleUpdateStatus(formData.id, formData.status, formData.tentative_date)}
                                                            >
                                                                <FaCheck />
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* TENTATIVE DATE FIELD */}
                                                    {showDateField && (
                                                        <div className='col-md-6 d-flex align-items-end justify-content-between'>
                                                            <TextField
                                                                type='date'
                                                                label="Tentative Date"
                                                                fullWidth
                                                                variant="outlined"
                                                                name='tentative_date'
                                                                required
                                                                value={formData.tentative_date}
                                                                InputLabelProps={{ shrink: true }}
                                                                onChange={handleChange}
                                                                size="small"
                                                                margin="normal"
                                                            />
                                                            {/* ✅ Check Button moves to the right of this field when shown */}
                                                            <button
                                                                className='ms-2'
                                                                style={{
                                                                    borderRadius: "5px",
                                                                    padding: '5px 15px',
                                                                    fontSize: '18px',
                                                                    border: 'none',
                                                                    backgroundColor: "#007bff",
                                                                    color: "#fff",
                                                                    height: '40px'
                                                                }}
                                                                onClick={() => handleUpdateStatus(formData.id, formData.status, formData.tentative_date)}
                                                            >
                                                                <FaCheck />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {/* customer status */}
                                            <div className='col-md-6 mb-2'>
                                                <TextField
                                                    label="Customer Response"
                                                    fullWidth
                                                    variant="outlined"
                                                    name="customer_response"
                                                    value={formData.customer_response}
                                                    size="small"
                                                    margin="normal"
                                                    disabled
                                                    sx={getCustomerResponseStyles(formData.customer_response)}
                                                />

                                            </div>


                                            <div className='col-md-6 mb-2'>
                                                <TextField
                                                    label="Category"
                                                    fullWidth
                                                    variant="outlined"
                                                    name='category_name'
                                                    value={formData.category_name}
                                                    onChange={handleChange}
                                                    size="small"
                                                    margin="normal"
                                                    disabled
                                                    sx={getDisabledInputStyles}
                                                >
                                                </TextField>
                                            </div>

                                            <div className='col-md-12 mb-2'>
                                                <TextField
                                                    label="Product Brand"
                                                    fullWidth
                                                    variant="outlined"
                                                    name='brand'
                                                    value={formData.brand}
                                                    onChange={handleChange}
                                                    size="small"
                                                    margin="normal"
                                                    disabled
                                                    sx={getDisabledInputStyles}
                                                />
                                            </div>

                                            <div className='col-md-12 mb-2'>
                                                <TextField
                                                    label="Product Name"
                                                    fullWidth
                                                    variant="outlined"
                                                    name='manual_product_name'
                                                    value={formData.manual_product_name}
                                                    onChange={handleChange}
                                                    size="small"
                                                    margin="normal"
                                                    disabled
                                                    sx={getDisabledInputStyles}
                                                />
                                            </div>

                                            <div className='col-md-12 mb-2'>
                                                <TextField
                                                    label="Service Title"
                                                    fullWidth
                                                    variant="outlined"
                                                    name='repair_title'
                                                    value={formData.repair_title}
                                                    onChange={handleChange}
                                                    size="small"
                                                    margin="normal"
                                                    disabled
                                                    sx={getDisabledInputStyles}
                                                />
                                            </div>

                                            <div className='col-md-12 mb-2'>
                                                <TextField
                                                    label="Service Description"
                                                    fullWidth
                                                    variant="outlined"
                                                    multiline
                                                    minRows={4}
                                                    name='description'
                                                    value={formData.description}
                                                    onChange={handleChange}
                                                    size="small"
                                                    margin="normal"
                                                    disabled
                                                    sx={getDisabledInputStyles}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className='col-md-4'>
                                        <div className='card border shadow-sm h-100' style={{ minHeight: '300px', maxHeight: '450px', minWidth: '300px', maxWidth: '450px' }}>
                                            <div className='card-body d-flex flex-column align-items-center justify-content-center'>

                                                <div style={{
                                                    width: '200px',
                                                    height: '200px',
                                                    position: 'relative',
                                                    bottom: '70px',
                                                    borderRadius: '5px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '10px', // Space between images
                                                    marginBottom: '20px' // Space below the image container
                                                }}>
                                                    <h4 className='mb-4 border-bottom pb-2 ms-4'>Product Image</h4> {/* Added margin-bottom */}
                                                    {formData.image1 && (
                                                        <img
                                                            src={formData.image1}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                borderRadius: '5px'
                                                            }}
                                                            alt="Product Image 1"
                                                        />
                                                    )}
                                                    {formData.image2 && (
                                                        <img
                                                            src={formData.image2}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%', // Changed to 100% to match first image
                                                                objectFit: 'cover',
                                                                borderRadius: '5px'
                                                            }}
                                                            alt="Product Image 2"
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ServiceScreen;

